import React, { useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft, Plus, Search } from "lucide-react";
import {
  createClientRole,
  getClient,
  listAccessPermissions,
  listClientRoles,
  updateClientRole,
} from "../../../api/chatbotApi";
import { authStore } from "../../../util/authStore";
import { isClientAdmin } from "../../../util/accessControl";

const items = (response) => Array.isArray(response?.data) ? response.data : [];
const emptyRole = { role_key: "", name: "", description: "", permissions: [] };

export default function ChatbotClientRolesPage() {
  const { clientId } = useParams();
  const user = authStore.getSnapshot().user;
  const authorized = isClientAdmin(user, clientId);
  const [roles, setRoles] = useState(null);
  const [search, setSearch] = useState("");
  const [clientName, setClientName] = useState("");
  const [catalog, setCatalog] = useState([]);
  const [editing, setEditing] = useState(null);
  const [formOpen, setFormOpen] = useState(false);
  const [form, setForm] = useState(emptyRole);
  const [error, setError] = useState("");
  const [saving, setSaving] = useState(false);
  const [revision, setRevision] = useState(0);

  useEffect(() => {
    let active = true;
    if (!authorized) return () => { active = false; };
    setClientName("");
    Promise.all([listClientRoles(clientId), listAccessPermissions(), getClient(clientId)])
      .then(([roleResponse, catalogResponse, client]) => {
        const roleList = items(roleResponse);
        const permissionList = items(catalogResponse);
        if (!Array.isArray(roleResponse?.data) || !Array.isArray(catalogResponse?.data)) {
          throw new Error("Invalid roles or permission catalog response.");
        }
        if (active) {
          setRoles(roleList);
          setCatalog(permissionList);
          setClientName(client.name);
        }
      })
      .catch((err) => { if (active) setError(err.response?.data?.detail || err.message); });
    return () => { active = false; };
  }, [authorized, clientId, revision]);

  const groupedCatalog = useMemo(() => catalog.reduce((groups, permission) => {
    const resource = permission.resource || permission.permission?.split(".")[0];
    if (!resource) return groups;
    groups[resource] = [...(groups[resource] || []), permission];
    return groups;
  }, {}), [catalog]);
  const searchTerm = search.trim().toLowerCase();
  const filteredRoles = (roles || []).filter((role) => (
    [role.name, role.role_key, role.description, ...(role.permissions || [])].some((value) => (
      value?.toLowerCase().includes(searchTerm)
    ))
  ));

  const startCreate = () => {
    setEditing(null);
    setForm(emptyRole);
    setFormOpen(true);
    setError("");
  };
  const startEdit = (role) => {
    setEditing(role);
    setFormOpen(true);
    setForm({
      role_key: role.role_key || "",
      name: role.name || "",
      description: role.description || "",
      permissions: Array.isArray(role.permissions) ? role.permissions : [],
    });
    setError("");
  };
  const togglePermission = (permission) => setForm((current) => ({
    ...current,
    permissions: current.permissions.includes(permission)
      ? current.permissions.filter((item) => item !== permission)
      : [...current.permissions, permission],
  }));
  const save = async (event) => {
    event.preventDefault();
    setError("");
    setSaving(true);
    try {
      const payload = {
        name: form.name.trim(),
        description: form.description.trim() || null,
        permissions: [...new Set(form.permissions)],
      };
      if (editing) await updateClientRole(clientId, editing.id, payload);
      else await createClientRole(clientId, { ...payload, role_key: form.role_key.trim() });
      setEditing(null);
      setFormOpen(false);
      setRevision((value) => value + 1);
    } catch (err) {
      setError(err.response?.data?.detail || err.message || "Unable to save role.");
    } finally {
      setSaving(false);
    }
  };
  const changeRoleStatus = async (role, changes) => {
    setSaving(true);
    setError("");
    try {
      await updateClientRole(clientId, role.id, changes);
      setRevision((value) => value + 1);
    } catch (err) {
      setError(err.response?.data?.detail || err.message || "Unable to update role.");
    } finally {
      setSaving(false);
    }
  };
  const permissionString = (entry) => entry.resource && entry.action
    ? `${entry.resource}.${entry.action}`
    : entry.permission;
  const selectPermissions = (entries) => setForm((current) => ({
    ...current,
    permissions: [...new Set([...current.permissions, ...entries.map(permissionString)])],
  }));
  const removePermissions = (entries) => {
    const keys = new Set(entries.map(permissionString));
    setForm((current) => ({
      ...current,
      permissions: current.permissions.filter((permission) => !keys.has(permission)),
    }));
  };

  if (!authorized) return <section className="rounded-xl bg-white p-8"><h1 className="text-xl font-bold">Access denied</h1><p className="mt-2 text-slate-600">Only Super Admins and Client Admins can manage client users and roles.</p></section>;

  return <section className="space-y-6">
    <nav aria-label="Breadcrumb" className="flex items-center gap-2 text-sm text-slate-500">
      <Link to="/clients" className="inline-flex items-center gap-1 transition hover:text-slate-900">
        <ArrowLeft size={16} /> Clients
      </Link>
      <span>/</span>
      <Link to={`/clients/${clientId}`} className="transition hover:text-slate-900">
        {clientName || "Client"}
      </Link>
      <span>/</span>
      <span aria-current="page" className="font-semibold text-slate-900">Roles</span>
    </nav>
    <div className="flex flex-wrap items-center justify-between gap-3">
      <div><h1 className="text-2xl font-bold">{clientName ? `${clientName}'s Roles & Permissions` : "Roles & Permissions"}</h1><p className="mt-1 text-sm text-slate-600">Create client-specific roles. Each grant is client-wide and grants only the exact resource action selected.</p></div>
      <div className="flex flex-wrap gap-2">
        <nav aria-label="Client access navigation" className="flex flex-wrap gap-2">
          <Link to={`/clients/${clientId}/roles`} aria-current="page" className="rounded-lg bg-cyan-800 px-4 py-2 text-sm font-semibold text-white">Roles</Link>
          <Link to={`/clients/${clientId}/users`} className="rounded-lg border border-slate-200 bg-white px-4 py-2 text-sm font-semibold text-slate-700">Users</Link>
        </nav>
      </div>
    </div>
    {error && <p role="alert" className="rounded-lg bg-rose-50 p-3 text-rose-700">{error}</p>}

    {formOpen && <form onSubmit={save} className="space-y-5 rounded-xl border bg-white p-5">
      <div className="flex items-center justify-between"><h2 className="text-lg font-bold">{editing ? `Edit ${editing.name}` : "Create custom role"}</h2><button type="button" onClick={() => { setEditing(null); setForm(emptyRole); setFormOpen(false); }}>Close</button></div>
      <div className="grid gap-4 md:grid-cols-2">
        {!editing && <label className="text-sm font-semibold">Role key<input required pattern="[a-z][a-z0-9_]{1,63}" title="Use lowercase letters, digits, and underscores." value={form.role_key} onChange={(event) => setForm({ ...form, role_key: event.target.value })} placeholder="client_manager" className="mt-1 w-full rounded-lg border p-2.5 font-normal" /></label>}
        <label className="text-sm font-semibold">Display name<input required maxLength={120} value={form.name} onChange={(event) => setForm({ ...form, name: event.target.value })} placeholder="Client Manager" className="mt-1 w-full rounded-lg border p-2.5 font-normal" /></label>
        <label className="text-sm font-semibold md:col-span-2">Description<textarea maxLength={1000} value={form.description} onChange={(event) => setForm({ ...form, description: event.target.value })} className="mt-1 w-full rounded-lg border p-2.5 font-normal" rows={2} /></label>
      </div>
      <div>
        <div className="mb-3 flex flex-wrap items-center justify-between gap-3">
          <h3 className="font-bold">Permission grid</h3>
          <div className="flex gap-2">
            <button type="button" disabled={saving || catalog.length === 0} onClick={() => selectPermissions(catalog)} className="rounded-lg border px-3 py-1.5 text-sm font-semibold disabled:opacity-50">Select all</button>
            <button type="button" disabled={saving || form.permissions.length === 0} onClick={() => setForm((current) => ({ ...current, permissions: [] }))} className="rounded-lg border px-3 py-1.5 text-sm font-semibold disabled:opacity-50">Remove all</button>
          </div>
        </div>
        {catalog.length === 0 ? <p className="text-sm text-slate-500">Loading permissions…</p> : <div className="grid items-start gap-4 lg:grid-cols-2">
          {Object.entries(groupedCatalog).map(([resource, permissions]) => <section key={resource} aria-label={`${resource.replaceAll("_", " ")} permissions`} className="min-w-0 overflow-hidden rounded-lg border">
            <div className="flex flex-wrap items-center justify-between gap-2 bg-slate-50 px-4 py-2">
              <h4 className="font-semibold capitalize">{resource.replaceAll("_", " ")}</h4>
              <div className="flex gap-3 text-xs font-semibold">
                <button type="button" disabled={saving} onClick={() => selectPermissions(permissions)} className="text-indigo-700 hover:underline disabled:opacity-50">Select all</button>
                <button type="button" disabled={saving} onClick={() => removePermissions(permissions)} className="text-slate-600 hover:underline disabled:opacity-50">Remove all</button>
              </div>
            </div>
            <div className="grid gap-2 p-3 sm:grid-cols-2">
              {permissions.map((entry) => {
                const key = permissionString(entry);
                return <label key={key} className="flex items-start gap-2 rounded-md p-2 text-sm hover:bg-slate-50">
                  <input type="checkbox" disabled={saving} className="mt-0.5" checked={form.permissions.includes(key)} onChange={() => togglePermission(key)} />
                  <span className="min-w-0 break-words"><span className="font-medium">{entry.action || key.split(".")[1]}</span><code className="block break-all text-xs text-slate-600">{key}</code>{entry.description && <span className="block text-xs text-slate-500">{entry.description}</span>}</span>
                </label>;
              })}
            </div>
          </section>)}
        </div>}
      </div>
      <div className="flex justify-end gap-2"><button type="button" onClick={() => { setEditing(null); setForm(emptyRole); setFormOpen(false); }} className="rounded-lg border px-4 py-2">Cancel</button><button disabled={saving} className="rounded-lg bg-indigo-700 px-4 py-2 font-semibold text-white disabled:opacity-50">{saving ? "Saving…" : editing ? "Save role" : "Create role"}</button></div>
    </form>}

    <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
      <h2 className="text-xl font-bold">Roles{roles !== null ? ` (${roles.length})` : ""}</h2>
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:gap-4">
        <div className="relative w-full sm:w-80">
          <Search size={18} aria-hidden="true" className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-slate-500" />
          <input type="search" aria-label="Search roles" placeholder="Search roles or permissions..." value={search} onChange={(event) => setSearch(event.target.value)} className="w-full rounded-lg border border-slate-300 bg-white py-2.5 pl-10 pr-3 text-sm" />
        </div>
        {!editing && <button type="button" onClick={startCreate} className="inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-lg bg-indigo-700 px-4 py-2.5 font-semibold text-white"><Plus size={18} aria-hidden="true" />Create role</button>}
      </div>
    </div>
    {roles === null ? <p role="status">Loading roles…</p> : roles.length === 0 ? <div className="rounded-xl border border-dashed bg-white p-10 text-center"><h2 className="font-semibold">No custom roles yet</h2><p className="mt-1 text-sm text-slate-500">Create a role, then assign it to one or more client users.</p></div> : filteredRoles.length === 0 ? <p role="status" className="rounded-xl border bg-white p-10 text-center text-slate-500">No roles match your search.</p> : <div className="overflow-x-auto rounded-xl border bg-white"><table className="w-full text-left">
      <thead className="bg-slate-50"><tr>{["Role", "Permissions", "Status", "Actions"].map((header) => <th key={header} className="p-3">{header}</th>)}</tr></thead>
      <tbody>{filteredRoles.map((role) => <tr key={role.id} className="border-t">
        <td className="p-3"><strong>{role.name}</strong><code className="ml-2 text-xs text-slate-500">{role.role_key}</code>{role.description && <p className="mt-1 text-sm text-slate-500">{role.description}</p>}</td>
        <td className="p-3 text-sm">{(role.permissions || []).length === 0 ? <span className="text-slate-500">No permissions</span> : <div className="flex flex-wrap gap-1">{role.permissions.map((permission) => <code key={permission} className="rounded bg-slate-100 px-2 py-1 text-xs">{permission}</code>)}</div>}</td>
        <td className="p-3">{role.deleted ? "Removed" : role.active === false ? "Inactive" : "Active"}</td>
        <td className="p-3"><div className="flex flex-wrap gap-2"><button type="button" onClick={() => startEdit(role)} className="font-semibold text-indigo-700">Edit</button>
          {role.deleted ? <button type="button" disabled={saving} onClick={() => changeRoleStatus(role, { deleted: false, active: true })}>Restore</button>
            : <><button type="button" disabled={saving} onClick={() => changeRoleStatus(role, { active: role.active === false })}>{role.active === false ? "Activate" : "Deactivate"}</button>
              <button type="button" disabled={saving} onClick={() => changeRoleStatus(role, { deleted: true, active: false })}>Remove</button></>}</div></td>
      </tr>)}</tbody>
    </table></div>}
  </section>;
}
